import isNil from 'lodash/isNil'

/**
 * The value displayed in case of a nil date
 */
export const datePlaceholder = '--/--/----'

/**
 * The value displayed in case of a nil time
 */
export const timePlaceholder = '--:--:--'

/**
 * The value displayed in case of a nil duration
 */
export const durationPlaceholder = '--:--:--'

/**
 * The value displayed in case of a nil datetime
 */
export const datetimePlaceholder = `${datePlaceholder} ${timePlaceholder}`

/**
 * The default react-intl date format.
 */
export const dateFormat = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  placeholder: datePlaceholder,
}

/**
 * The default react-intl time format.
 */
export const timeFormat = {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  placeholder: timePlaceholder,
}

/**
 * The default react-intl datetime format.
 */
export const dateTimeFormat = {
  ...dateFormat,
  ...timeFormat,
  placeholder: datetimePlaceholder,
}

/**
 * Formats a date according to the given options.
 * https://github.com/yahoo/react-intl/wiki/API#formatdate
 *
 * @param intl The react-intl service
 * @param date The date to format
 * @param placeholder The placeholder to display in case of nil date
 * @param options The react-intl date options
 * @return {String} The formatted date
 */
export const formatDate = (intl, date, { placeholder, ...options } = dateFormat) =>
  formatDateTime(intl, date, { placeholder, ...options })

/**
 * Formats a time according to the given options.
 * https://github.com/yahoo/react-intl/wiki/API#formattime
 *
 * @param intl The react-intl service
 * @param time The time to format
 * @param placeholder The placeholder to display in case of nil time
 * @param options The react-intl time options
 * @return {String} The formatted time
 */
export const formatTime = (intl, time, { placeholder, ...options } = timeFormat) =>
  isNil(time) ? placeholder : intl.formatTime(time, options)

/**
 * Formats a datetime according to the given options.
 * https://github.com/yahoo/react-intl/wiki/API#formatdate
 *
 * @param intl The react-intl service
 * @param datetime The datetime to format
 * @param placeholder The placeholder to display in case of nil datetime
 * @param options The react-intl datetime options
 * @return {String} The formatted datetime
 */
export const formatDateTime = (intl, datetime, { placeholder, ...options } = dateTimeFormat) =>
  isNil(datetime) ? placeholder : intl.formatDate(datetime, options)
